// INV33 (docs/build-briefs/INV33-usage-and-waste.md; DEC-290): the pure,
// DOM-free wording behind the Usage and waste screen (ui/src/usageWaste.ts),
// kept apart so a plain node:test file can import it
// (test/ui-usage-waste-model.test.ts).
//
// Bottles first, ounces beneath (DEC-290), the way the stock value report
// reads: a bottle or keg item shows "4.6 bottles" with its ounces under it, a
// whole-unit item shows units, and an item with no bottle size shows ounces
// only. The server sends both figures for every column; only the small detail
// lines divide ounces by the item's own ounces per container for display.

import type { UsageWasteContainersDto, UsageWasteItemDto } from "./api.ts";
import { categoryLabel } from "./catalogDisplay.ts";

export type QuantityColumn =
  "start" | "received" | "used" | "wasted" | "vendorReturned" | "counts" | "other" | "end";

/** The ounce (or unit) figure of one column, from the row. */
export function columnValue(item: UsageWasteItemDto, column: QuantityColumn): string | null {
  return column === "other" ? item.otherTotal : item[column];
}

export function groupDigits(whole: string): string {
  return whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/** A decimal string as text with `places` decimals and thousands separators. */
export function decimalText(value: string, places: number): string {
  const number = Number(value);
  const negative = number < 0 && Number(Math.abs(number).toFixed(places)) !== 0;
  const fixed = Math.abs(number).toFixed(places);
  const [whole, fraction] = fixed.split(".") as [string, string | undefined];
  return `${negative ? "-" : ""}${groupDigits(whole)}${fraction === undefined ? "" : `.${fraction}`}`;
}

function isZero(value: string): boolean {
  return Number(value) === 0;
}

function plural(word: string, rounded: string): string {
  return rounded === "1.0" || rounded === "1" || rounded === "-1.0" || rounded === "-1"
    ? word
    : `${word}s`;
}

/** The one bottle rounding every report uses (UX76): two decimals under 0.1
 * bottle, one decimal otherwise. */
export function bottleFigure(value: string): string {
  const n = Math.abs(Number(value));
  return decimalText(value, n !== 0 && n < 0.1 ? 2 : 1);
}

function containerText(value: string): string {
  return bottleFigure(value);
}

export interface QuantityText {
  /** "-" for a quiet zero. */
  main: string;
  /** The ounces under a bottle figure, or null. */
  beneath: string | null;
  zero: boolean;
}

export function quantityText(
  item: UsageWasteItemDto,
  column: QuantityColumn,
  options: { signed?: boolean; showZero?: boolean } = {},
): QuantityText {
  const value = columnValue(item, column);
  if (value === null) {
    return { main: "-", beneath: null, zero: true };
  }
  if (isZero(value) && options.showZero !== true) {
    return { main: "-", beneath: null, zero: true };
  }
  const sign = options.signed === true && Number(value) > 0 ? "+" : "";
  if (item.unit === "unit") {
    const text = decimalText(value, 0);
    return { main: `${sign}${text} ${plural("unit", text)}`, beneath: null, zero: isZero(value) };
  }
  const oz = `${sign}${decimalText(value, 1)} oz`;
  const containerValue =
    item.containers === null ? null : item.containers[column as keyof UsageWasteContainersDto];
  if (item.containerUnit !== null && containerValue !== null && containerValue !== undefined) {
    const text = containerText(containerValue);
    return {
      main: `${sign}${text} ${plural(item.containerUnit, text)}`,
      beneath: oz.replace(/^\+/, ""),
      zero: isZero(value),
    };
  }
  return { main: oz, beneath: null, zero: isZero(value) };
}

/** Waste as a share of used, as "3.4% of used", or null when nothing was used
 * or nothing was wasted. The warning is 5% or more (the approved design). */
export function wastePercent(item: UsageWasteItemDto): { text: string; warn: boolean } | null {
  if (item.used === null || item.wasted === null) return null;
  const used = Number(item.used);
  const wasted = Number(item.wasted);
  if (!(used > 0) || !(wasted > 0)) return null;
  const pct = (wasted / used) * 100;
  return { text: `${pct.toFixed(1)}% of used`, warn: pct >= 5 };
}

/** One sentence naming the figures of an item the ledger holds in more than one unit. */
export function mixedUnitsSentence(item: UsageWasteItemDto): string {
  const parts = item.breakdown.map((b) => `${decimalText(b.quantity, 1)} ${b.unit}`);
  const held = parts.length === 0 ? "more than one unit" : parts.join(" and ");
  return `The records hold ${held} for this item, so the figures cannot be added up. A stock count fixes it. Left out of the totals above.`;
}

/** The two-line sum of a total: ounces, then units, whichever are not zero. */
export function totalLines(oz: string, units: string, signed = false): string[] {
  const lines: string[] = [];
  const sign = (v: string): string => (signed && Number(v) > 0 ? "+" : "");
  if (!isZero(oz)) lines.push(`${sign(oz)}${decimalText(oz, 1)} oz`);
  if (!isZero(units)) {
    const text = decimalText(units, 0);
    lines.push(`${sign(units)}${text} ${plural("unit", text)}`);
  }
  return lines;
}

export function percentOf(part: string, whole: string): string {
  const w = Number(whole);
  return w > 0 ? `${((Number(part) / w) * 100).toFixed(1)}%` : "0.0%";
}

/** The shipped liquor categories have labels; a category a location added
 * itself is only a stored key ("cans_and_bottles"), so say it in words. */
export function categoryName(key: string): string {
  const known = categoryLabel(key);
  if (known !== key) {
    return known;
  }
  const spaced = key.replace(/_/g, " ").trim();
  return spaced.length === 0 ? key : spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** A part of a row (a detail line) in words, bottles first when the item has a
 * bottle size: "0.1 bottles (2.0 oz)". */
export function inlineQuantity(
  item: UsageWasteItemDto,
  value: string,
  options: { signed?: boolean } = {},
): string {
  const sign = options.signed === true && Number(value) > 0 ? "+" : "";
  if (item.unit === "unit") {
    const text = decimalText(value, 0);
    return `${sign}${text} ${plural("unit", text)}`;
  }
  const oz = `${decimalText(value, 1)} oz`;
  if (item.containerUnit !== null && item.ouncesPerContainer !== null) {
    const per = Number(item.ouncesPerContainer);
    if (per > 0) {
      const text = containerText(String(Number(value) / per));
      return `${sign}${text} ${plural(item.containerUnit, text)} (${oz})`;
    }
  }
  return `${sign}${oz}`;
}
